import {
  Component,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  forwardRef,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  OnInit,
  Input,
  Output,
  EventEmitter,
  HostListener
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Subject, fromEvent } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { RebirthNGConfig } from '../rebirth-ng.config';
import { DocumentRef, WindowRef } from '../window-ref';

@Component({
  selector: 're-slider',
  template: "\n    <div class=\"slider-body\"><div class=\"slider-container\"><div #slider class=\"slider\"><div class=\"base-line\" (click)=\"onSliderClick($event)\"><div class=\"active-line\" [style.width.px]=\"handleOffset\"></div></div></div><div #sliderDot [style.left.px]=\"handleOffset\" class=\"handle\" [attr.title]=\"value\"></div></div><div class=\"slider-operator-container\"><button class=\"btn reduce\" (click)=\"onOperateValue(value - 1)\">-</button> <button class=\"btn plus\" (click)=\"onOperateValue(value + 1)\">+</button></div></div>\n  ",
  styles: ["\n    :host{display:block;position:relative;overflow:hidden;width:100%;box-sizing:border-box}.slider-body{display:inline-block;position:relative;padding:0 45px;height:30px;width:100%}.slider-container{position:relative;display:inline-block;width:100%;height:100%;user-select:none;z-index:1}.slider{position:relative;height:100%;width:100%}.base-line{height:6px;width:100%;border-radius:3px;background-color:#ddd;position:absolute;left:0;top:50%;margin-top:-3px;overflow:hidden}.active-line{height:100%;width:0;background-color:#2196f3;position:relative;left:0}.handle{background-color:#2196f3;width:18px;height:18px;border-radius:50%;cursor:pointer;position:absolute;top:50%;margin-top:-9px;border:3px solid #fff;box-sizing:border-box;margin-left:-9px}.slider-operator-container{color:#2196f3}.slider-operator-container .btn{background:0 0;position:absolute;outline:0;box-shadow:none;font-size:30px;padding-left:20px;padding-right:20px}.slider-operator-container .btn:focus,.slider-operator-container .btn:hover{color:#2196f3}.slider-operator-container .btn.reduce{left:0;top:-18px}.slider-operator-container .btn.plus{right:0;top:-19px}\n  "],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'slider',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderComponent),
      multi: true
    }
  ]
})
export class SliderComponent implements AfterViewInit, OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('sliderDot') dot: ElementRef;
  @ViewChild('slider') slider: ElementRef;
  @Output() onChange: EventEmitter<number> = new EventEmitter<number>();

  handleOffset = 0;

  @Input() max;
  @Input() min;
  private offset: number;
  private stepWidth: number;

  value = 0;

  private complete$ = new Subject<void>();

  private onValueChange = (_: any) => null;
  private onTouched = () => null;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private rebirthNGConfig: RebirthNGConfig,
              private documentRef: DocumentRef,
              private windowRef: WindowRef) {
    this.max = rebirthNGConfig.slider.max;
    this.min = rebirthNGConfig.slider.min;
  }

  ngOnInit() {
    this.offset = this.max - this.min;
  }

  ngOnDestroy() {
    this.complete$.complete();
  }

  ngAfterViewInit() {
    this.stepWidth = this.windowRef.getOffsetWidth(this.slider) / this.offset;

    const mousedown$ = fromEvent(this.dot.nativeElement, 'mousedown');
    const mousemove$ = fromEvent(this.documentRef.document, 'mousemove');
    const mouseup$ = fromEvent(this.documentRef.document, 'mouseup');

    mousedown$
      .pipe(
        switchMap(() => {
          this.onTouched();
          return mousemove$.pipe(takeUntil(mouseup$));
        }),
        takeUntil(this.complete$)
      )
      .subscribe((event: MouseEvent) => {
        event.preventDefault();
        this.calcValueWhenMove(event);
        this.changeDetectorRef.markForCheck();
      });

    mousedown$
      .pipe(
        switchMap(() => {
          this.onTouched();
          return mouseup$.pipe(take(1));
        }),
        takeUntil(this.complete$)
      )
      .subscribe((event: MouseEvent) => {
        event.preventDefault();
        this.calcValueWhenMove(event);

        this.calcHandleOffset();
        this.changeDetectorRef.markForCheck();
      });
  }

  @HostListener('window:resize')
  private onResize() {
    this.stepWidth = this.windowRef.getOffsetWidth(this.slider) / this.offset;
    this.calcHandleOffset();
  }

  public onSliderClick(event) {
    this.calcValueWhenMove(event);
  }

  public onOperateValue(value: number): void {
    this.onTouched();
    this.operateValue(value);
  }

  public operateValue(value: number): void {
    this.changeValue(value);
    this.calcHandleOffset();
  }

  private calcHandleOffset(): void {
    this.handleOffset = this.value * this.stepWidth;
  }

  private calcValueWhenMove(event: MouseEvent): void {
    const rect = this.windowRef.getBoundingClientRect(this.slider)// IE >= 9
    const mouseRelativeSliderOffsetX = event.clientX - rect.left;
    const fixedOffsetX = Math.max(Math.min(mouseRelativeSliderOffsetX, rect.width), 0);

    this.handleOffset = fixedOffsetX;

    this.changeValue(Math.round(fixedOffsetX / this.stepWidth));
  }

  private changeValue(newValue): void {
    const fixedValue = Math.max(Math.min(newValue, this.max), 0);
    if (fixedValue === this.value) {
      return;
    }
    this.value = fixedValue;
    const outSideValue = fixedValue + this.min;
    this.onValueChange(outSideValue);
    this.onChange.emit(outSideValue);
  }

  writeValue(value: number) {
    const sideValue = value ? value - this.min : this.min;
    this.operateValue(sideValue);
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: any) {
    this.onValueChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
}
